// Downscale + compress an image File in the browser before upload.
// Prevents large phone photos (5-12MB) from failing against request-size limits,
// and speeds up uploads. Returns a JPEG File, or the original on any failure.
export async function compressImage(file, { maxDim = 1920, quality = 0.82, keepPng = false } = {}) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    if (file.type === "image/gif") return file; // keep animated gifs intact
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = dataUrl;
    });
    const { width, height } = img;
    if (!width || !height) return file;
    // Already small enough — leave it alone.
    if (width <= maxDim && height <= maxDim && file.size < 1.5 * 1024 * 1024) return file;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const png = keepPng && file.type === "image/png";
    const outType = png ? "image/png" : "image/jpeg";
    const blob = await new Promise((res) => canvas.toBlob(res, outType, quality));
    if (!blob || blob.size >= file.size) return file; // no gain — keep original
    const name = (file.name || "image").replace(/\.\w+$/, "") + (png ? ".png" : ".jpg");
    return new File([blob], name, { type: outType });
  } catch {
    return file;
  }
}
